import { buildApiUrl } from '../config';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const apiRequest = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options
  };
  
  let lastError;
  const maxRetries = options.maxRetries || 0;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        const backoffDelay = 1000 * Math.pow(2, attempt - 1);
        await sleep(backoffDelay);
      }
      
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.mensaje || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return {
        success: true,
        data,
        error: data.error,
        mensaje: data.mensaje,
        status: response.status,
        attempts: attempt + 1
      };
      
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        break;
      }
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        continue;
      }
      
      if (error.message.includes('401') || error.message.includes('403')) {
        break;
      }
    }
  }
  
  throw new Error(`Error en petición API después de ${maxRetries + 1} intentos: ${lastError.message}`);
};

const validateEventData = (eventData) => {
  const errors = [];
  
  const titulo = eventData.Titulo || eventData.titulo;
  const ubicacion = eventData.Ubicacion || eventData.ubicacion;
  const fechaInicio = eventData.Fecha_creacion || eventData.fechaCreacion || eventData.fechaInicio;
  const fechaFin = eventData.Fecha_fin || eventData.fechaFin;
  
  if (!titulo || titulo.trim().length === 0) {
    errors.push('El título es requerido');
  }
  
  if (titulo && titulo.length > 100) {
    errors.push('El título no puede exceder 100 caracteres');
  }
  
  if (!ubicacion || ubicacion.trim().length === 0) {
    errors.push('La ubicación es requerida');
  }
  
  if (ubicacion && ubicacion.length > 250) {
    errors.push('La ubicación no puede exceder 250 caracteres');
  }
  
  if (!fechaInicio) {
    errors.push('La fecha de inicio es requerida');
  }
  
  if (!fechaFin) {
    errors.push('La fecha de fin es requerida');
  }
  
  if (fechaInicio && fechaFin) {
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    
    if (fin <= inicio) {
      errors.push('La fecha de fin debe ser posterior a la fecha de inicio');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

const eventService = {
  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.fechaDesde) queryParams.append('fechaDesde', filters.fechaDesde);
    if (filters.fechaHasta) queryParams.append('fechaHasta', filters.fechaHasta);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `eventos?${queryString}` : 'eventos';
    
    return apiRequest(endpoint);
  },
  
  getById: async (id) => {
    if (!id) {
      throw new Error('ID de evento es requerido');
    }
    
    return apiRequest(`evento/${id}`);
  },
  
  create: async (eventData) => {
    const validation = validateEventData(eventData);
    
    if (!validation.isValid) {
      throw new Error(`Datos inválidos: ${validation.errors.join(', ')}`);
    }
    
    const dataToCreate = {
      titulo: eventData.Titulo || eventData.titulo,
      fechaCreacion: eventData.Fecha_creacion || eventData.fechaCreacion || eventData.fechaInicio,
      fechaFin: eventData.Fecha_fin || eventData.fechaFin,
      mes: eventData.Mes || eventData.mes,
      ubicacion: eventData.Ubicacion || eventData.ubicacion,
      url_File: eventData.Url_File || eventData.url_File || eventData.imagenUrl,
      fileName: eventData.FileName || eventData.fileName
    };
    
    return apiRequest('evento', {
      method: 'POST',
      body: JSON.stringify(dataToCreate)
    });
  },
  
  update: async (id, eventData) => {
    if (!id) {
      throw new Error('ID de evento es requerido');
    }
    
    const validation = validateEventData(eventData);
    
    if (!validation.isValid) {
      throw new Error(`Datos inválidos: ${validation.errors.join(', ')}`);
    }
    
    const dataToUpdate = {
      titulo: eventData.Titulo || eventData.titulo,
      fechaCreacion: eventData.Fecha_creacion || eventData.fechaCreacion || eventData.fechaInicio,
      fechaFin: eventData.Fecha_fin || eventData.fechaFin,
      mes: eventData.Mes || eventData.mes,
      ubicacion: eventData.Ubicacion || eventData.ubicacion,
      url_File: eventData.Url_File || eventData.url_File || eventData.imagenUrl,
      fileName: eventData.FileName || eventData.fileName
    };
    
    return apiRequest(`evento/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dataToUpdate)
    });
  },
  
  delete: async (id) => {
    if (!id) {
      throw new Error('ID de evento es requerido');
    }
    
    return apiRequest(`evento/${id}`, {
      method: 'DELETE'
    });
  },
  
  uploadImageAndCreateEvent: async (eventData, imageFile, options = {}) => {
    try {
      let imageUrl = eventData.Url_File || eventData.url_File || eventData.imagenUrl || null;
      
      if (imageFile) {
        const imageService = (await import('./imageService')).default;
        const uploadResult = await imageService.upload(imageFile, {
          onProgress: options.onImageProgress,
          maxRetries: 3
        });
        
        if (uploadResult.success) {
          imageUrl = uploadResult.url;
        } else {
          throw new Error('Error al subir la imagen');
        }
      }
      
      const eventDataWithImage = {
        ...eventData,
        url_File: imageUrl,
        fileName: imageFile ? imageFile.name : (eventData.FileName || eventData.fileName || null)
      };
      
      return await eventService.create(eventDataWithImage);
      
    } catch (error) {
      throw new Error(`Error al crear evento con imagen: ${error.message}`);
    }
  },
  
  uploadImageAndUpdateEvent: async (id, eventData, imageFile, options = {}) => {
    try {
      let imageUrl = eventData.Url_logo || eventData.imagenUrl;
      
      if (imageFile) {
        const imageService = (await import('./imageService')).default;
        const uploadResult = await imageService.upload(imageFile, {
          onProgress: options.onImageProgress,
          maxRetries: 3
        });
        
        if (uploadResult.success) {
          imageUrl = uploadResult.url;
        } else {
          throw new Error('Error al subir la imagen');
        }
      }
      
      const eventDataWithImage = {
        ...eventData,
        url_File: imageUrl,
        fileName: imageFile ? imageFile.name : null
      };
      
      return await eventService.update(id, eventDataWithImage);
      
    } catch (error) {
      throw new Error(`Error al actualizar evento con imagen: ${error.message}`);
    }
  },
  
  validateEvent: validateEventData
};

export default eventService;
